import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  OnroerendeZaak,
  OnroerendeZaakLeegstand,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {AbstractControl, FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {optionsLandcodesMetNLD} from '../../../gedeelde-componenten/gedeelde-componenten.component';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type OnroerendeZaakFormGroup = FormGroup<{
  adres: AdresFormGroup;
  woz: FormControl<number | null>;
  woznummer: FormControl<string | null>;
  aanhorigheid: FormControl<boolean | null>;
  percentageEigendom: FormControl<number | null>;
  bewoondePeriode: PeriodeFormGroup;
  leegstand: FormControl<OnroerendeZaakLeegstand | null>,
  datumGekocht: FormControl<Date | null>;
  aankoopkosten: FormControl<number | null>;
  eigenWoningSchuldVerkochteWoning: FormControl<number | null>;
  datumVerkocht: FormControl<Date | null>;
  verkoopkosten: FormControl<number | null>;
  betaaldeErfpachtCanon: FormControl<number | null>;
  soortPand: FormControl<boolean | null>;
}>;

type AdresFormGroup = FormGroup<{
  straat: FormControl<string | null>;
  huisNummer: FormControl<number | null>;
  huisNummerToevoeging: FormControl<string | null>;
  postcode: FormControl<string | null>;
  woonplaats: FormControl<string | null>;
  landCode: FormControl<string | null>;
}>;

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-eigenwoningen',
    templateUrl: './eigenwoningen.component.html',
    styleUrls: ['./eigenwoningen.component.scss'],
    imports: [SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiButtonModule, SharedUiDialogModule, CommonModule],
})
export class EigenwoningenComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    protected readonly OnroerendeZaakLeegstand = OnroerendeZaakLeegstand;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();
    public index = 0;
    public dialogVisible = false;

    public formGroup = new FormGroup({
        eigenWoningen: new FormArray<OnroerendeZaakFormGroup>([]),
    });

    public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' },
    ];

    public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

    public redenLeegstand: SelectFormControlItem<OnroerendeZaakLeegstand | null>[] = [
        { value: null, label: '--- selecteer ---' },
        { value: OnroerendeZaakLeegstand.VERKOOP, label: 'Woning staat/stond te koop en wordt/is verkocht' },
        { value: OnroerendeZaakLeegstand.AANBOUW, label: 'Woning is/was in aanbouw/bouwkavel' },
        { value: OnroerendeZaakLeegstand.EX, label: 'Woning is/was bewoond door ex-partner' },
        { value: OnroerendeZaakLeegstand.VERZORGINGSTEHUIS, label: 'Onbewoond door vertrek naar verzorgingstehuis' },
        { value: OnroerendeZaakLeegstand.UITZENDING, label: 'Onbewoond door uitzending naar buitenland' },
    ];

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public ngOnInit(): void {
        this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((entity) => {
            this.updateForm(entity.aangifte?.box1?.belastbareInkomstenUitEigenWoning?.eigenWoningen ?? []);
        });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractRelevant = () =>
            this.eigenWoningen.getRawValue().map((w) => ({
                woz: w.woz,
                betaaldeErfpachtCanon: w.betaaldeErfpachtCanon,
                begindatum: w.bewoondePeriode?.begindatum,
                einddatum: w.bewoondePeriode?.einddatum,
                percentageEigendom: w.percentageEigendom,
            }));

        this.changeSubscriptions.add(
            this.eigenWoningen.valueChanges
                .pipe(
                    debounceTime(500),
                    tap(() => this.saveForm()),
                    map(() => extractRelevant()),
                    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                    switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(),
        );
    }

    showDialog(index: number) {
        this.index = index;
        this.dialogVisible = true;
    }

    get eigenWoningen(): FormArray<OnroerendeZaakFormGroup> {
        return this.formGroup.controls.eigenWoningen;
    }

    addEigenWoning(): void {
        this.eigenWoningen.push(this.createFormGroup(), { emitEvent: false });
    }

    deleteEigenWoning(index: number): void {
        this.eigenWoningen.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
    }

    createFormGroup(item?: OnroerendeZaak): FormGroup {
        return new FormGroup({
            adres: new FormGroup({
                straat: new FormControl<string | null>(item?.adres?.straat ?? null),
                huisNummer: new FormControl<number | null>(item?.adres?.huisNummer ?? null),
                huisNummerToevoeging: new FormControl<string | null>(item?.adres?.huisNummerToevoeging ?? null),
                postcode: new FormControl<string | null>(this.formatPostcode(item?.adres?.postcode) ?? null),
                woonplaats: new FormControl<string | null>(item?.adres?.woonplaats ?? null),
                landCode: new FormControl<string | null>(item?.adres?.landCode ?? 'NLD'),
            }),
            woz: new FormControl<number | null>(item?.woz ?? null),
            woznummer: new FormControl<string | null>(item?.woznummer ?? null),
            aanhorigheid: new FormControl<boolean | null>(item?.aanhorigheid ?? false),
            percentageEigendom: new FormControl<number | null>(item?.percentageEigendom ?? 100),
            bewoondePeriode: new FormGroup({
                begindatum: new FormControl<Date | null>(item?.bewoondePeriode?.begindatum ?? null),
                einddatum: new FormControl<Date | null>(item?.bewoondePeriode?.einddatum ?? null),
            }),
            leegstand: new FormControl<OnroerendeZaakLeegstand | null>(item?.leegstand ?? null),
            datumGekocht: new FormControl<Date | null>(item?.datumGekocht ?? null),
            aankoopkosten: new FormControl<number | null>(item?.aankoopkosten?.[0]?.bedrag ?? null),
            datumVerkocht: new FormControl<Date | null>(item?.datumVerkocht ?? null),
            eigenWoningSchuldVerkochteWoning: new FormControl<number | null>(item?.eigenWoningSchuldVerkochteWoning ?? null),
            verkoopkosten: new FormControl<number | null>(item?.verkoopkosten?.[0]?.bedrag ?? null),
            betaaldeErfpachtCanon: new FormControl<number | null>(item?.betaaldeErfpachtCanon ?? null),
            soortPand: new FormControl<boolean | null>(item?.soortPand ?? null),
        });
    }

    private updateForm(lijst: OnroerendeZaak[]): void {
        this.syncFormArray(lijst, this.eigenWoningen, (item) => this.createFormGroup(item));
    }

    private syncFormArray<T, G extends AbstractControl>(lijst: T[], formArray: FormArray<G>, factory: (item: T) => G): void {
        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }
        lijst.forEach((item, i) => {
            if (i < formArray.length) {
                formArray.at(i).patchValue(item, { emitEvent: false });
            } else {
                formArray.push(factory(item), { emitEvent: false });
            }
        });
    }

    saveForm(): void {
        this.formGroup.updateValueAndValidity({ emitEvent: false });

        const woningen: OnroerendeZaak[] = this.formGroup
            .getRawValue()
            .eigenWoningen.filter((item) => item.adres?.straat?.trim() || item.woz != null)
            .map((item) => ({
                adres: {
                    straat: item.adres?.straat ?? undefined,
                    huisNummer: item.adres?.huisNummer ?? undefined,
                    huisNummerToevoeging: item.adres?.huisNummerToevoeging ?? undefined,
                    postcode: item.adres?.postcode ?? undefined,
                    woonplaats: item.adres?.woonplaats ?? undefined,
                    landCode: item.adres?.landCode ?? undefined,
                },
                woz: item.woz ?? 0,
                woznummer: item.woznummer ?? undefined,
                aanhorigheid: item.aanhorigheid ?? false,
                percentageEigendom: item.percentageEigendom ?? 100,
                bewoondePeriode: {
                    begindatum: item.bewoondePeriode?.begindatum!,
                    einddatum: item.bewoondePeriode?.einddatum!,
                },
                leegstand: item.leegstand ?? undefined,
                datumGekocht: item.datumGekocht ?? undefined,
                aankoopkosten: item.aankoopkosten != null ? [{ bedrag: item.aankoopkosten, omschrijving: 'Aankoopkosten' }] : undefined,
                datumVerkocht: item.datumVerkocht ?? undefined,
                eigenWoningSchuldVerkochteWoning: item.eigenWoningSchuldVerkochteWoning ?? undefined,
                verkoopkosten: item.verkoopkosten != null ? [{ bedrag: item.verkoopkosten, omschrijving: 'Verkoopkosten' }] : undefined,
                betaaldeErfpachtCanon: item.betaaldeErfpachtCanon ?? undefined,
                soortPand: item.soortPand ?? false,
            }));

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            return {
                ...currentEntity,
                aangifte: {
                    ...currentEntity.aangifte,
                    box1: {
                        ...currentEntity.aangifte.box1,
                        belastbareInkomstenUitEigenWoning: {
                            ...currentEntity.aangifte.box1?.belastbareInkomstenUitEigenWoning,
                            eigenWoningen: woningen,
                        },
                    },
                },
            } as IncomeTaxEntity;
        });
    }

    formatPostcode(postcode: string | null | undefined): string {
        if (!postcode) return ''; // Retourneer een lege string als postcode null of undefined is

        // Verwijder alle niet-alfanumerieke tekens (bijv. spaties)
        const cleanedPostcode = postcode.replace(/[^a-zA-Z0-9]/g, '');

        // Zorg voor 4 cijfers en 2 hoofdletters
        if (cleanedPostcode.length >= 4) {
            const cijfers = cleanedPostcode.substring(0, 4).replace(/\D/g, ''); // Alleen cijfers
            const letters = cleanedPostcode
                .substring(4, 6)
                .replace(/[^a-zA-Z]/g, '')
                .toUpperCase(); // Alleen letters
            return `${cijfers}${letters}`;
        }
        return cleanedPostcode;
    }

    protected readonly Date = Date;
}
